import { api } from '@/api/client';
import { mapAgentApiMessageToUiMessage } from '@/components/Messenger/agent/agentChatMessageMap';
import {
  agentChatReplaceAllFromApi,
  type AgentChatProcessingPhaseClient,
} from '@/components/Messenger/cache/agentChatCacheStore';
import { resetAgentChatStreamState } from '@/components/Messenger/cache/agentChatStreamStore';
import { setAgentChatProcessingFlag } from '@/components/Messenger/cache/agentChatsProcessingStore';

const AGENT_CHATS_RECONNECT_LIST_LIMIT = 3;

export type RefreshAgentChatAfterReconnectResult = {
  processing: boolean;
  processingPhase: AgentChatProcessingPhaseClient;
};

/**
 * Refetch persisted agent messages + processing flag after WS/visibility recover.
 * Does not resume SSE tokens — only restores DB-backed state and clears a stuck liveStream
 * when the server reports processing === false.
 */
export async function refreshAgentChatAfterReconnect(
  chatId: string
): Promise<RefreshAgentChatAfterReconnectResult | null> {
  const id = chatId.trim();
  if (!id) return null;

  const res = await api.agentChats.messages(id, { limit: 100 });
  const ui = res.messages.map(mapAgentApiMessageToUiMessage);
  const processing = Boolean(res.processing);
  const processingPhase: AgentChatProcessingPhaseClient = processing
    ? (res.processing_phase ?? null)
    : null;

  agentChatReplaceAllFromApi(id, ui, {
    lastReadUiMessageId: res.last_read_message_id ?? null,
    processing,
    processingPhase,
  });
  setAgentChatProcessingFlag(id, processing);

  if (!processing) {
    resetAgentChatStreamState(id);
  }

  return { processing, processingPhase };
}

/** Quietly sync sidebar processing flags for the first page of agent chats. */
export async function syncAgentChatsProcessingAfterReconnect(): Promise<void> {
  const res = await api.agentChats.list({ limit: AGENT_CHATS_RECONNECT_LIST_LIMIT });
  for (const row of res.chats) {
    const id = row.id.trim();
    if (!id) continue;
    setAgentChatProcessingFlag(id, Boolean(row.processing));
  }
}
